import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi, getErrorMessage, type Cosmetic } from '../lib/api';

export default function Cosmetics() {
  const [cosmetics, setCosmetics] = useState<Cosmetic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [deleteError, setDeleteError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminApi.listCosmetics(typeFilter || undefined);
      setCosmetics(data.cosmetics);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [typeFilter]);

  const handleDelete = async (id: string) => {
    if (!confirm(`Delete cosmetic "${id}"? This will soft-delete it (is_active = 0).`)) return;
    setDeleteError('');
    try {
      await adminApi.deleteCosmetic(id);
      await load();
    } catch (err) {
      setDeleteError(getErrorMessage(err));
    }
  };

  if (loading) return <div className="text-gray-400">Loading cosmetics...</div>;
  if (error) return <div className="text-red-400">Error: {error}</div>;

  const cosmeticTypes = [
    'crown', 'badge', 'frame', 'accent',
    'name_style', 'name_frame', 'avatar_frame', 'rank_badge',
    'leaderboard_background', 'profile_theme', 'entrance_animation',
    'title_prefix', 'mascot_companion',
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Cosmetics Catalog</h2>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm"
        >
          <option value="">All Types</option>
          {cosmeticTypes.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>

      {deleteError && (
        <div className="bg-red-900/20 border border-red-800/50 text-red-200 rounded p-3 text-sm">
          {deleteError}
        </div>
      )}

      {cosmetics.length === 0 ? (
        <p className="text-gray-500">No cosmetics found.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-400 border-b border-gray-800">
              <th className="py-2">Emoji</th>
              <th>ID</th>
              <th>Type</th>
              <th>Acquire</th>
              <th>Display</th>
              <th className="text-right">Sold</th>
              <th className="text-right">Limit</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {cosmetics.map((c) => (
              <tr key={c.id} className="border-b border-gray-800/50">
                <td className="py-2 text-lg">{c.emoji}</td>
                <td className="font-mono text-xs">{c.id}</td>
                <td>{c.type}</td>
                <td>{c.acquire}</td>
                <td>{c.display_context}</td>
                <td className="text-right">{c.stock_sold}</td>
                <td className="text-right text-gray-400">{c.stock_limit ?? '∞'}</td>
                <td>
                  <span
                    className={`text-xs px-2 py-0.5 rounded ${
                      c.is_active ? 'bg-green-900 text-green-300' : 'bg-gray-800 text-gray-400'
                    }`}
                  >
                    {c.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div className="flex gap-2">
                    <Link
                      to={`/cosmetics/${encodeURIComponent(c.id)}/preview`}
                      className="text-xs text-blue-400 hover:text-blue-300"
                    >
                      Preview
                    </Link>
                    <button
                      onClick={() => handleDelete(c.id)}
                      className="text-xs text-red-400 hover:text-red-300"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
